import { Effect, Fiber, Semaphore } from 'effect';

/**
 * One permit, held for the whole mutation, so writes against the cart run one
 * after another instead of racing. A shop can reject overlapping writes or
 * commit them in an order that differs from the shopper's actions.
 *
 * A browser session creates one permit for its cart commands. Server code must
 * scope a permit to one shopper, because a process-wide permit would serialize
 * unrelated shoppers.
 */
type CartMutationPermit = <A, E, R>(effect: Effect.Effect<A, E, R>) => Effect.Effect<A, E, R>;

/**
 * Builds a permit that runs one effect at a time.
 *
 * An interrupted request does not prove the shop stopped its write. Each effect runs in a
 * detached fiber: interrupting the caller stops the wait, never the write, and
 * the permit stays taken until the backend has answered. `Atom.fn` interrupts
 * its previous run on every new call, which is exactly the interruption this
 * guards against. The detached fiber starts interruptible, so a timeout inside
 * the effect still works.
 */
export const makeCartMutationPermit = (): CartMutationPermit => {
  const semaphore = Semaphore.makeUnsafe(1);

  return (effect) =>
    Effect.forkDetach(Semaphore.withPermit(semaphore)(effect)).pipe(Effect.flatMap(Fiber.join));
};
